import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from 'redux/root.reducer';
import {
  MapDrawFuncParams,
  CFCProps,
  MAP_DATA_SERVICE_DATA_SET,
  LandParcelType
} from 'types';
import Terraformer from 'terraformer-arcgis-parser';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { zoomToFeature, restoreViewToClonedShape } from 'components/Map/helper';
import { getGeocodeStart, clearGeocode } from 'redux/map/actions';
import {
  setShape,
  setEnquiryAddress,
  fetchLodgeEnquiryInit,
  setLiveArea
} from 'redux/lodgeEnquiry';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import {
  addGraphic,
  geocodeToAddress,
  addClonedShapeToMap,
  removeClonedShapeLayer
} from './drawingFunction';
import { findLayerById } from 'utilities/map-instance.util';
import { addLayerToMap } from 'utilities/map-instance.util';
import { fetchOrgListByBoundary } from 'redux/members';
import MapLocationSearch from 'components/MapLocationSearchV2';
import MapTools from './tools';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import {
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import { useComponentMount } from 'utilities/hooks.util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getNewDate, toTitleCase } from 'utilities/helper.util';
import { DrawingTooltip } from 'components';
import GeoscapeTestWidget, {
  GeoscapeApproach,
  WidgetPosition
} from 'components/GeoscapeTestWidget';
import { debugLog } from 'utilities/basemap-debug.util';

dayjs.extend(utc);

export const MapView: React.FC<Props> = (props) => {
  const {
    user,
    drawedShape,
    clonedEnquiryId,
    enquiriesLayerExtent,
    geocode,
    activeTool,
    activateEnquiryForm,
    onClickTools,
    toolExpanded,
    onExpandTools,
    presetsStatus,
    setMapReadyParent,
    prePopulatedData,
    baseMapTheme,
    shapeArea
  } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  // Check URL query params for clone parameter as fallback
  const urlParams = new URLSearchParams(location.search);
  const cloneParamFromUrl = urlParams.get('clone');
  // Use clonedEnquiryId from Redux, but fallback to URL param if Redux hasn't loaded yet
  const effectiveClonedEnquiryId =
    clonedEnquiryId ||
    (cloneParamFromUrl ? parseInt(cloneParamFromUrl, 10) : null);

  const alertGroup = 'map-alert';
  const extraMapModules = [
    MapSettings.modules.featureLayer,
    MapSettings.modules.webMercatorUtils
  ];
  const [waitingGeoData, setWaitingGeoData] = useState<boolean>(false);
  const [mapReady, setMapReady] = useState<boolean>(false);
  const activeToolRef = useRef(activeTool);
  const baseMapThemeRef = useRef(baseMapTheme);
  const lastZoomedExtentRef = useRef<any>(null);
  const expectingShapeRef = useRef<boolean>(false); // Track if we're expecting a shape to load
  const drawedShapeRef = useRef<any>(drawedShape); // Track current drawedShape value
  const [showDrawingTooltip, setShowDrawingTooltip] = useState<boolean>(false);
  const [showGeoscapeWidget, setShowGeoscapeWidget] = useState<boolean>(false);
  const [widgetPosition, setWidgetPosition] = useState<WidgetPosition>(
    WidgetPosition.BOTTOM_RIGHT
  );
  const [, setGeoscapeApproach] = useState<GeoscapeApproach>(
    GeoscapeApproach.ORIGINAL
  );

  // Update baseMapThemeRef when baseMapTheme changes
  useEffect(() => {
    baseMapThemeRef.current = baseMapTheme;
  }, [baseMapTheme]);

  useEffect(() => {
    // Update ref whenever drawedShape changes
    drawedShapeRef.current = drawedShape;
    // If drawedShape is set, reset expectingShapeRef since we have the shape
    if (drawedShape) {
      expectingShapeRef.current = false;
    }
  }, [drawedShape]);

  useEffect(() => {
    debugLog('[MapView] useEffect for drawedShape:', {
      hasDrawedShape: !!drawedShape,
      mapReady,
      clonedEnquiryId,
      effectiveClonedEnquiryId,
      cloneParamFromUrl,
      presetsStatus,
      shapeArea,
      expectingShape: expectingShapeRef.current,
      hadShapeRef: !!drawedShapeRef.current,
      timestamp: new Date().toISOString()
    });

    // If we have a clonedEnquiryId (from Redux or URL), we're expecting a shape to be loaded
    if (effectiveClonedEnquiryId && !drawedShape) {
      debugLog('[MapView] Cloned enquiry detected, expecting shape to load', {
        clonedEnquiryId,
        effectiveClonedEnquiryId,
        cloneParamFromUrl,
        mapReady,
        timestamp: new Date().toISOString()
      });
      expectingShapeRef.current = true;
      // Ensure the layer exists even if shape isn't loaded yet
      // This prevents the layer from being removed when duplicating enquiry the second time
      if (mapReady) {
        const cloneShapeLayer: any = findLayerById('cloned-shape-layer');
        debugLog('[MapView] Checking for cloned-shape-layer:', {
          layerExists: !!cloneShapeLayer,
          graphicsCount: cloneShapeLayer?.graphics?.length || 0,
          timestamp: new Date().toISOString()
        });
        if (!cloneShapeLayer) {
          debugLog(
            '[MapView] Creating cloned-shape-layer in anticipation of shape'
          );
          const { GraphicsLayer } = window.loadedMapModules;
          const newLayer = new GraphicsLayer({ id: 'cloned-shape-layer' });
          addLayerToMap(newLayer);
          debugLog('[MapView] Created and added cloned-shape-layer');
        }
      } else {
        debugLog('[MapView] Map not ready yet, cannot create layer');
      }
    }

    if (drawedShape) {
      debugLog('[MapView] drawedShape exists, processing:', {
        shapeType: drawedShape.type,
        expectingShape: expectingShapeRef.current,
        timestamp: new Date().toISOString()
      });
      expectingShapeRef.current = false; // Shape is loaded, no longer expecting
      dispatch(fetchOrgListByBoundary(drawedShape));
      if (mapReady) {
        // Always render the shape when it exists and map is ready
        // Zoom to shape after draw/clone; skip when returning from presets (keep prior view).
        const shouldZoomToShape = presetsStatus !== 'returned';
        debugLog(
          '[MapView] Calling addClonedShapeToMap with shouldZoomToShape:',
          shouldZoomToShape,
          { timestamp: new Date().toISOString() }
        );
        addClonedShapeToMap(
          drawedShape,
          shouldZoomToShape,
          shapeArea,
          baseMapTheme
        );
      } else {
        debugLog('[MapView] Map not ready yet, skipping shape render');
      }
    } else {
      debugLog('[MapView] No drawedShape, checking if should wait:', {
        expectingShape: expectingShapeRef.current,
        hadShapeRef: !!drawedShapeRef.current,
        clonedEnquiryId,
        timestamp: new Date().toISOString()
      });
      // Don't remove layer immediately if we're expecting a shape to load OR if we just had a shape
      // This prevents removing the layer when duplicating an enquiry the second time
      // where drawedShape might be temporarily undefined while enquiry data is being fetched
      // Also prevents removing layer if drawedShape becomes undefined briefly after being set
      const shouldWait =
        expectingShapeRef.current ||
        (effectiveClonedEnquiryId && drawedShapeRef.current);

      if (shouldWait) {
        debugLog(
          '[MapView] No drawedShape but expecting shape or had shape recently, waiting before removing layer',
          {
            expectingShape: expectingShapeRef.current,
            hadShape: !!drawedShapeRef.current,
            clonedEnquiryId,
            effectiveClonedEnquiryId,
            cloneParamFromUrl,
            timestamp: new Date().toISOString()
          }
        );
        const removeLayerTimeout = setTimeout(() => {
          // Check current drawedShape value from ref (not stale closure value)
          // Only remove if shape is still missing AND we're not expecting it anymore
          debugLog('[MapView] Timeout callback executing:', {
            drawedShapeRef: !!drawedShapeRef.current,
            expectingShapeRef: expectingShapeRef.current,
            clonedEnquiryId,
            timestamp: new Date().toISOString()
          });
          if (!drawedShapeRef.current) {
            // Double-check if we should still wait (maybe shape is being loaded)
            if (effectiveClonedEnquiryId) {
              // If we have clonedEnquiryId, give it more time
              debugLog(
                '[MapView] Still no drawedShape but have clonedEnquiryId, keeping layer for now',
                {
                  effectiveClonedEnquiryId,
                  cloneParamFromUrl,
                  timestamp: new Date().toISOString()
                }
              );
            } else {
              debugLog(
                '[MapView] No drawedShape after delay and no clonedEnquiryId, removing cloned shape layer',
                { timestamp: new Date().toISOString() }
              );
              expectingShapeRef.current = false;
              removeClonedShapeLayer();
              dispatch(clearGeocode());
            }
          } else {
            debugLog('[MapView] Shape found after delay, keeping layer', {
              timestamp: new Date().toISOString()
            });
          }
        }, 2000); // Increased delay to 2 seconds to allow enquiry data to load

        return () => {
          debugLog('[MapView] Cleaning up timeout', {
            timestamp: new Date().toISOString()
          });
          clearTimeout(removeLayerTimeout);
        };
      } else {
        debugLog(
          '[MapView] No drawedShape and not expecting, removing cloned shape layer',
          { timestamp: new Date().toISOString() }
        );
        removeClonedShapeLayer();
        dispatch(clearGeocode());
      }
    }
  }, [
    dispatch,
    drawedShape,
    clonedEnquiryId,
    effectiveClonedEnquiryId,
    cloneParamFromUrl,
    mapReady,
    presetsStatus,
    shapeArea,
    location.search,
    baseMapTheme
  ]);

  useEffect(() => {
    setWaitingGeoData(false);
    if (geocode) {
      if (typeof geocode !== 'string') {
        const address = geocodeToAddress(geocode);
        dispatch(setEnquiryAddress(address));
      } else {
        dispatch(
          setEnquiryAddress({
            line1: '',
            line2: '',
            country: 'AUS',
            locality: '',
            postcode: '',
            state: ''
          })
        );
      }
    }
  }, [dispatch, geocode, setWaitingGeoData]);

  useEffect(() => {
    if (
      enquiriesLayerExtent &&
      typeof enquiriesLayerExtent !== 'string' &&
      !enquiriesLayerExtent.noRecord
    ) {
      // Don't zoom if user is actively drawing or has a drawing tool selected
      const isDrawingToolActive = activeTool !== 'selector';

      // Don't zoom if we've already zoomed to this extent
      // Compare extent values instead of references since Redux creates new objects
      const lastExtent = lastZoomedExtentRef.current;
      const isSameExtent =
        lastExtent &&
        lastExtent.xmin === enquiriesLayerExtent.xmin &&
        lastExtent.xmax === enquiriesLayerExtent.xmax &&
        lastExtent.ymin === enquiriesLayerExtent.ymin &&
        lastExtent.ymax === enquiriesLayerExtent.ymax;

      // Don't zoom to enquiries extent if there's a saved map extent (user was viewing a specific location)
      const hasSavedExtent =
        window.mapOriginalExtent &&
        (window.mapOriginalExtent.xmin !== enquiriesLayerExtent.xmin ||
          window.mapOriginalExtent.xmax !== enquiriesLayerExtent.xmax ||
          window.mapOriginalExtent.ymin !== enquiriesLayerExtent.ymin ||
          window.mapOriginalExtent.ymax !== enquiriesLayerExtent.ymax);

      if (
        presetsStatus !== 'returned' &&
        !clonedEnquiryId &&
        !prePopulatedData?.shape &&
        !drawedShape &&
        !isDrawingToolActive &&
        !isSameExtent &&
        !hasSavedExtent
      ) {
        zoomToFeature(enquiriesLayerExtent);
        lastZoomedExtentRef.current = enquiriesLayerExtent;
      }
    }
  }, [
    enquiriesLayerExtent,
    clonedEnquiryId,
    presetsStatus,
    prePopulatedData,
    activeTool,
    drawedShape
  ]);

  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  useEffect(() => {
    // Only set up the global function in debug mode
    if (process.env.REACT_APP_GEOSCAPE_DEBUG === 'true') {
      // Set up the global function to show/hide the Geoscape test widget
      const showGeoscapeWidgetFn = (
        show: boolean,
        position?: WidgetPosition
      ) => {
        setShowGeoscapeWidget(show);
        if (position) {
          setWidgetPosition(position);
        }
      };
      const windowObj: any = window;
      windowObj.showGeoscapeWidget = showGeoscapeWidgetFn;

      // Cleanup function
      return () => {
        const windowObj: any = window;
        delete windowObj.showGeoscapeWidget;
      };
    }
  }, []);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup(alertGroup));
      dispatch(clearFeatureLayerLayerExtent());
      dispatch(setShape({ shape: undefined, area: undefined }));
    };
  });

  const handleOnUseLandParcel = (parcel: LandParcelType) => {
    const theme = baseMapThemeRef.current;
    const { loadedMapModules, loadedMap, loadedView } = window;

    if (
      loadedMap &&
      loadedMapModules &&
      loadedView &&
      typeof parcel.shape !== 'string'
    ) {
      const { Graphic, WebMercatorUtils } = loadedMapModules;
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      const geometry: any = Terraformer.convert(parcel.shape);
      const webMercatorGeometry = WebMercatorUtils.geographicToWebMercator(
        geometry
      );

      const symbolToUse =
        theme === 'dark'
          ? MapSettings.polygonSymbolDarkCompleteDraw
          : MapSettings.polygonSymbol;

      const graphic = new Graphic({
        geometry: {
          ...webMercatorGeometry,
          type: 'polygon'
        },
        symbol: symbolToUse
      });

      const { polygonInGeoJson, geometryArea } = addGraphic(
        { graphic, drawingLayer, graphics: undefined },
        'polygon',
        theme
      );

      dispatch(setShape({ shape: polygonInGeoJson, area: geometryArea }));
      if (parcel.address) {
        dispatch(
          setEnquiryAddress({
            line1: toTitleCase(parcel.address.line1 || ''),
            line2: '',
            country: 'AUS',
            locality: toTitleCase(parcel.address.locality || ''),
            postcode: parcel.address.postcode,
            state: parcel.address.state
          })
        );
      }
      // Cancel the sketch tool but don't clear the drawing layer
      // since we just added the parcel polygon to it
      if (window.loadedSketchViewModel) {
        window.loadedSketchViewModel.cancel();
      }
      loadedView.closePopup();
    } else if (parcel.shape === 'no shape') {
      dispatch(
        addAlert(
          alertGroup,
          'error',
          'Sorry, we did not find a parcel boundary for this address.',
          'Please sketch your job site with the drawing tools.'
        )
      );
    }
  };

  const onCompleteDrawing = (params: MapDrawFuncParams) => {
    const drawingTool = activeToolRef.current;
    const theme = baseMapThemeRef.current;
    onClickTools('selector');
    dispatch(setLiveArea(undefined));
    setShowDrawingTooltip(false);
    try {
      const { polygonInGeoJson, centroid, geometryArea } = addGraphic(
        params,
        drawingTool,
        theme
      );
      const coords = polygonInGeoJson.coordinates[0];
      const xmin = coords.reduce(
        (agg: number, v: number[]) => (v[0] < agg ? v[0] : agg),
        180
      );
      const xmax = coords.reduce(
        (agg: number, v: number[]) => (v[0] > agg ? v[0] : agg),
        -180
      );
      const ymin = coords.reduce(
        (agg: number, v: number[]) => (v[1] < agg ? v[1] : agg),
        90
      );
      const ymax = coords.reduce(
        (agg: number, v: number[]) => (v[1] > agg ? v[1] : agg),
        -90
      );

      const AUSTRALIA_BBOX = {
        xmin: +95,
        xmax: +170,
        ymin: -45,
        ymax: -7
      };
      if (
        xmin > AUSTRALIA_BBOX.xmax ||
        xmax < AUSTRALIA_BBOX.xmin ||
        ymin > AUSTRALIA_BBOX.ymax ||
        ymax < AUSTRALIA_BBOX.ymin
      ) {
        const err = new Error('The job site must be within Australia');
        err.name = 'Illegal job site';
        throw err;
      }

      dispatch(setShape({ shape: polygonInGeoJson, area: geometryArea }));
      setWaitingGeoData(true);
      dispatch(
        getGeocodeStart({
          ...centroid,
          datasets: MAP_DATA_SERVICE_DATA_SET.GEOCODEAU
        })
      );
    } catch (e) {
      if (e instanceof Error) {
        dispatch(addAlert(alertGroup, 'error', e.name, e.message));
      }
    }
  };

  const onDrawing = (params: MapDrawFuncParams) => {
    const geometryArea = getLiveArea(params);
    geometryArea > 0 && dispatch(setLiveArea(geometryArea));
    setShowDrawingTooltip(true);
  };

  const onCancelDrawing = () => {
    const { loadedMap, loadedSketchViewModel } = window;

    // Clear any graphics from the drawing layer
    if (loadedMap && loadedSketchViewModel) {
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      if (drawingLayer) {
        // Cancel any active drawing operation
        loadedSketchViewModel.cancel();
        // Remove all graphics from the drawing layer
        drawingLayer.removeAll();
      }
    }

    onClickTools('selector');
    dispatch(setLiveArea(undefined));
    setShowDrawingTooltip(false);
  };

  const handleOnClickTools = (tool: string) => {
    onClickTools(tool);
    setShowDrawingTooltip(true);
  };

  const getLiveArea = ({ graphic, graphics }: MapDrawFuncParams) => {
    const { loadedMapModules } = window;
    const { GeometryEngine } = loadedMapModules;
    const areaUnit = 'square-kilometers';
    const lengthUnit = 'kilometers';
    const drawedGraphic = graphics !== undefined ? graphics[0] : graphic;

    if (drawedGraphic.geometry.type === 'polyline') {
      return GeometryEngine.geodesicLength(drawedGraphic.geometry, lengthUnit);
    } else {
      return GeometryEngine.geodesicArea(drawedGraphic.geometry, areaUnit);
    }
  };

  /**
   * onMapReady callback - initializes the map with enquiry layers and shape.
   *
   * WHY baseMapTheme is NOT in the dependency array:
   * We use baseMapThemeRef (defined at component top) instead of including baseMapTheme
   * directly to prevent the callback from being recreated when the basemap changes.
   * Recreating onMapReady triggers the Map component's onReady effect, which re-initializes
   * the enquiry flow and can cause unintended form submissions (SENT-2684).
   */
  const onMapReady = useCallback(() => {
    const last7days = getNewDate().subtract(7, 'day').startOf('day');

    const { loadedMap, loadedMapModules } = window;
    if (loadedMap && loadedMapModules) {
      const { Config, FeatureLayer } = loadedMapModules;
      if (Config?.request) {
        Config.request.interceptors.push({
          urls: getFeatureServerUrl('Enquiries'),
          before: featureRequestInterceptors
        });
      }

      if (FeatureLayer) {
        const featureLayer = new FeatureLayer({
          id: 'recent-enquiries-layer',
          url: getFeatureServerUrl('Enquiries'),
          outFields: ['objectid'],
          definitionExpression: `created_at > TIMESTAMP '${last7days
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')}' AND user_id = ${user.id}${
            clonedEnquiryId ? ` AND objectid <> ${clonedEnquiryId}` : ''
          }`,
          minScale: MapSettings.shapeMinScale,
          visible: false
        });

        loadedMap.add(featureLayer);

        featureLayer.when(() => {
          dispatch(queryFeatureLayerExtentStart(featureLayer));
        });
      }
    }
    dispatch(fetchLodgeEnquiryInit());
    setMapReady(true);
    setMapReadyParent(true);

    // After map is ready (including after dual loading), re-add shape if it exists
    // This ensures the shape persists through basemap changes
    if (drawedShape) {
      debugLog('[onMapReady] Map ready, re-adding shape after dual loading');
      const shouldZoomToShape = presetsStatus !== 'returned';
      // Use setTimeout to ensure map instance is fully ready
      setTimeout(() => {
        // Use ref to get current theme value without causing callback recreation
        addClonedShapeToMap(
          drawedShape,
          shouldZoomToShape,
          shapeArea,
          baseMapThemeRef.current
        );

        // Additional safeguard: Verify the shape was actually added after a short delay
        // This handles cases where dual loading might happen after onMapReady
        setTimeout(() => {
          const cloneShapeLayer: any = findLayerById('cloned-shape-layer');
          if (
            !cloneShapeLayer ||
            !cloneShapeLayer.graphics ||
            cloneShapeLayer.graphics.length === 0
          ) {
            debugLog(
              '[onMapReady] Shape layer missing or empty after delay, re-adding'
            );
            addClonedShapeToMap(
              drawedShape,
              shouldZoomToShape,
              shapeArea,
              baseMapThemeRef.current
            );
          } else {
            debugLog(
              '[onMapReady] Shape layer verified with graphics:',
              cloneShapeLayer.graphics.length
            );
            if (shouldZoomToShape) {
              restoreViewToClonedShape(shapeArea);
            }
          }
        }, 500);
      }, 100);
    }
  }, [
    dispatch,
    clonedEnquiryId,
    setMapReadyParent,
    user.id,
    drawedShape,
    presetsStatus,
    shapeArea
    // Note: baseMapTheme is intentionally excluded - we use baseMapThemeRef instead
    // to prevent onMapReady recreation on basemap change (SENT-2684)
  ]);

  return (
    <section className="map--view">
      <Map
        onDrawing={onDrawing}
        onCompleteDrawing={onCompleteDrawing}
        onCancelDrawing={onCancelDrawing}
        onReady={onMapReady}
        extraModules={extraMapModules}
        showbaseMapButton={true}
        maxZoomLevel={20}
      />
      {!activateEnquiryForm && (
        <>
          <MapTools
            toolExpanded={toolExpanded}
            onExpandTools={() => onExpandTools()}
            activeTool={activeTool}
            allButtonDisabled={
              !mapReady || activateEnquiryForm || waitingGeoData
            }
            onClickTools={(tool: string) => handleOnClickTools(tool)}
            removeButtonDisabled={!drawedShape}
          />
          {toolExpanded && (
            <MapLocationSearch
              selectSearchTooltipPosition="top"
              noOpenButton={true}
              onUseLandParcel={handleOnUseLandParcel}
              onCancelLandParcel={() => onClickTools('polygon')}
            />
          )}
        </>
      )}
      {showDrawingTooltip && (
        <DrawingTooltip
          view={window.loadedView}
          sketchView={window.loadedSketchViewModel}
          sketchToolType={activeTool}
          setShowDrawingTooltip={setShowDrawingTooltip}
        />
      )}
      {process.env.REACT_APP_GEOSCAPE_DEBUG === 'true' && (
        <GeoscapeTestWidget
          visible={showGeoscapeWidget}
          position={widgetPosition}
          onApproachChange={setGeoscapeApproach}
        />
      )}
    </section>
  );
};

const mapStateToProps = ({
  enquiresCache,
  lodgeEnquiryCache,
  mapCache,
  session
}: RootState) => ({
  drawedShape: lodgeEnquiryCache.shape || enquiresCache?.data?.enquiry?.shape,
  clonedEnquiryId: enquiresCache?.data?.enquiry?.id,
  enquiriesLayerExtent: mapCache.featureLayerExtent,
  geocode: mapCache.geocode,
  user: session?.currentUser,
  screenSize: session.screenSize,
  prePopulatedData: lodgeEnquiryCache.prePopulatedData,
  presetsStatus: lodgeEnquiryCache.presets?.status,
  shapeArea:
    lodgeEnquiryCache?.shapeArea ||
    enquiresCache?.data?.enquiry?.shapeArea / 1000000
});

const connector = connect(mapStateToProps);

type ownProps = {
  activeTool: string;
  activateEnquiryForm: boolean;
  onClickTools: Function;
  toolExpanded: boolean;
  onExpandTools: Function;
  setMapReadyParent: Function;
  baseMapTheme: string;
};

type Props = CFCProps<typeof connector> & ownProps;

MapView.displayName = 'HomeMapViewModule';

export default React.memo(connector(MapView));
